import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  AppBar,
  Button,
  ButtonProps,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  allSelectedOption,
  defaultSelectedDataOption,
  ISelectedDataOption,
  TP_COLORS,
} from '../../../constants';
import {
  createNewSubmissionBySubTopic,
  getSubmissionState,
} from '../../../store/SubmissionSlice';
import {
  getGroupedSubTopics,
  getSubTopicById,
  getSubTopicState,
} from '../../../store/SubTopicSlice';
import { getAuthState } from '../../../store/AuthSlice';
import {
  checkAllPapersBySubTopicId,
  getQuizBySubTopicId,
  getQuizState,
  resetQuiz,
} from '../../../store/QuizSlice';
import {
  getDiagnosticBySubTopicId,
  getDiagnosticState,
  resetDiagnostic,
} from '../../../store/DiagnosticSlice';
import { useAppSelector } from '../../../hooks';
import {
  checkUserPermission,
  constructOptionsFromArray,
  getUrlConvertedString,
} from '../../../helper';
import MobileHeader from '../../../components/Mobile/Header';
import BasicLoader from '../../../components/Loaders/BasicLoader';
import TopicHeader from '../../../components/Headers/TopicHeader';
import { getMetaTagState } from '../../../store/MetaTagSlice';

const diagnosticButtonProps: ButtonProps['sx'] = {
  size: 'small',
  color: 'inherit',
  variant: 'contained',
  sx: {
    color: TP_COLORS.black,
    backgroundColor: TP_COLORS.white,
    borderRadius: '16px',
    fontWeight: '600',
  },
};

const ViewSubmissionsBySubTopic: React.FC = () => {
  const params = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Store
  const subTopicState = useAppSelector(getSubTopicState);
  const submissionState = useAppSelector(getSubmissionState);
  const quizState = useAppSelector(getQuizState);
  const authState = useAppSelector(getAuthState);
  const diagnosticState = useAppSelector(getDiagnosticState);
  const metaTagState = useAppSelector(getMetaTagState);

  const { diagnostic } = diagnosticState;
  const subTopicData = subTopicState.subTopic;
  const topicData =
    subTopicData && subTopicData.topic ? subTopicData.topic : undefined;
  const quizCount =
    subTopicData && subTopicData.quizCount ? subTopicData.quizCount : undefined;

  // Permissions
  const createSubmissionPermission = checkUserPermission(authState.user, {
    submission: ['create'],
  });

  // State
  const [paperData, setPaperData] = useState({ ...defaultSelectedDataOption });
  const [redirect, setRedirect] = useState(false);

  const urlStringPattern = params.pattern || '';
  const urlStringPatternDivided = urlStringPattern.split('_').reverse();
  const subTopicId = urlStringPatternDivided[0] || '';
  const hasResources =
    subTopicData && subTopicData.resources && subTopicData.resources.length;

  const queryStringParams = new URLSearchParams(
    `?${location.hash.substring(1)}`
  );

  const urlPaper = queryStringParams.get('paper') || '';
  const isAllPapersAvailable = quizState?.allPapers;

  let curriculum = '';
  let urlConvertedCurriculum = '';
  let subject = '';
  let urlConvertedSubject = '';
  let topic = '';
  let subTopic = '';
  let urlConvertedSubTopic = '';
  let metaTitle = '';
  let metaDesc =
    'We have a wide range of resources to help you prepare for your';

  if (topicData) {
    subject = topicData.subject;
    topic = topicData.name;
    curriculum = topicData.curriculum;
    urlConvertedCurriculum = getUrlConvertedString(curriculum);
    urlConvertedSubject = getUrlConvertedString(subject);

    metaTitle += `${curriculum}`;
    metaDesc += ` ${curriculum}`;
    metaDesc += ' exams. Start practicing our';

    metaTitle += ` | ${subject}`;
    metaDesc += ` ${subject}`;
  }

  if (subTopicData) {
    subTopic = subTopicData.name;
    urlConvertedSubTopic = getUrlConvertedString(subTopic);

    metaTitle += ` | ${subTopic}`;
    metaDesc += ` ${subTopic}`;

    metaTitle += ' | Tutopiya';
    metaDesc += ' questions today!';
  }

  const isDiagnostic = topic === 'Diagnostic Challenge';

  useEffect(() => {
    dispatch(resetQuiz());

    return () => {
      dispatch(resetQuiz());
      dispatch(resetDiagnostic());
    };
  }, []);

  useEffect(() => {
    if (subTopicId) {
      dispatch(getGroupedSubTopics({ subTopicId }));
      dispatch(getSubTopicById(subTopicId));
      dispatch(checkAllPapersBySubTopicId(subTopicId));
    }
  }, [subTopicId]);

  useEffect(() => {
    if (topicData) {
      const newPaperData: ISelectedDataOption = {
        ...defaultSelectedDataOption,
      };

      newPaperData.options = constructOptionsFromArray(
        topicData.papers,
        'name',
        'name'
      );

      if (isAllPapersAvailable && newPaperData.options.length > 1) {
        newPaperData.options.unshift(allSelectedOption);
      }

      if (newPaperData.options.length) {
        // eslint-disable-next-line prefer-destructuring
        newPaperData.selectedItem = newPaperData.options[0];

        if (urlPaper) {
          const matchItem = newPaperData.options.find(
            (item) => item.value === urlPaper
          );

          if (matchItem) {
            newPaperData.selectedItem = matchItem;
          }
        }
      }

      setPaperData(newPaperData);
    }
  }, [subTopicData, isAllPapersAvailable]);

  useEffect(() => {
    if (quizState.quiz && redirect) {
      navigate(
        `${process.env.PUBLIC_URL}/dashboard/quiz-history/submissions/${
          quizState.quiz?._id || ''
        }/${urlConvertedCurriculum}_${urlConvertedSubject}_${urlConvertedSubTopic}_${subTopicId}`
      );
    }
  }, [quizState.quiz?._id]);

  useEffect(() => {
    if (
      subTopicId &&
      isDiagnostic &&
      diagnosticState.status !== 'loading' &&
      authState.user
    ) {
      dispatch(
        getDiagnosticBySubTopicId({ subTopicId, userId: authState.user._id })
      );
    }
  }, [isDiagnostic]);

  const handlePaperChange = (event: SelectChangeEvent) => {
    const { value } = event.target;

    const newPaperData: ISelectedDataOption = {
      ...paperData,
      selectedItem: { label: value, value },
    };

    setPaperData(newPaperData);
  };

  const onAttempt = () => {
    if (subTopicData && quizCount) {
      dispatch(
        createNewSubmissionBySubTopic({
          subTopicId: subTopicData._id,
          paper: paperData.selectedItem.value,
        })
      );
    }
  };

  const onViewHistory = () => {
    if (subTopicData) {
      dispatch(
        getQuizBySubTopicId({
          subTopicId: subTopicData._id,
          paper: paperData.selectedItem.value,
          redirectToquizHistory: true,
        })
      );

      setRedirect(true);
    }
  };

  return (
    <div>
      {!metaTagState.metaTags ||
        (!metaTagState.metaTags[location.pathname.replace(/[/]$/, '')] && (
          <Helmet>
            <title>{metaTitle}</title>
            <meta name="description" content={metaDesc} />
          </Helmet>
        ))}
      <MobileHeader
        leftContents={{
          type: 'subtopic-nav-bar',
        }}
      />
      <TopicHeader
        subject={subject}
        subTopics={[subTopic]}
        topic={topic}
        isGetSubTopic={false}
      />
      {subTopicState.status === 'loading' ||
      quizState.allPapersStatus === 'loading' ? (
        <div style={{ padding: '30px 0' }}>
          <BasicLoader />
        </div>
      ) : (
        <>
          {hasResources ? (
            <AppBar
              position="static"
              color="secondary"
              style={{
                background: `url('${process.env.PUBLIC_URL}/images/resourceLib/app-bar-bg.png') center right/contain no-repeat, #2C2C2C`,
              }}
            >
              <Toolbar>
                <Button
                  size="small"
                  variant="contained"
                  color="inherit"
                  onClick={() =>
                    navigate(
                      location.pathname.replace(
                        '/subtopic/',
                        '/resources/subtopic/'
                      )
                    )
                  }
                  style={{
                    color: TP_COLORS.black,
                    backgroundColor: TP_COLORS.white,
                    minWidth: '100px',
                  }}
                >
                  Find Resources
                </Button>
              </Toolbar>
            </AppBar>
          ) : null}
          <Stack alignItems="center" style={{ padding: '30px 0' }}>
            {diagnostic && diagnostic._id && (
              <Alert
                severity="info"
                sx={{
                  backgroundColor: TP_COLORS.blue.dark,
                  color: 'white',
                  borderRadius: '25px',
                  marginBottom: '15px',
                }}
                action={
                  <Button
                    {...diagnosticButtonProps}
                    onClick={() => {
                      navigate(
                        `${process.env.PUBLIC_URL}/dashboard/my-quizzes/diagnostic/${subTopicId}/${diagnostic._id}/${params.pattern}`
                      );
                    }}
                  >
                    Click here
                  </Button>
                }
              >
                Check your previous assessment report
              </Alert>
            )}

            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
              <img
                src={`${process.env.PUBLIC_URL}/images/quiz-setup-wizard-with-girl.png`}
                alt="start-quiz"
                width="100%"
                style={{ maxWidth: '80%', width: '400px' }}
              />
            </div>
            <div style={{ marginBottom: '30px' }}>
              <Typography
                component="h3"
                color="secondary"
                textAlign="center"
                sx={{
                  maxWidth: { xs: 'unset', sm: '680px' },
                  padding: '0 15px',
                }}
              >
                Before you begin the quiz, make sure you have a strong WIFI
                connection, a pen and paper to do any extra calculations and a
                water bottle or drink to get you thinking.
              </Typography>
            </div>
            <div style={{ marginBottom: '15px', minWidth: '300px' }}>
              <Select
                size="small"
                fullWidth
                value={paperData.selectedItem.value}
                onChange={handlePaperChange}
                disabled={
                  !quizCount || (isDiagnostic && paperData.options.length <= 1)
                }
              >
                {quizCount
                  ? paperData.options.map((paperOption, index) => (
                      <MenuItem key={index} value={paperOption.label}>
                        {paperOption.value}
                      </MenuItem>
                    ))
                  : null}
              </Select>
            </div>
            {createSubmissionPermission && (
              <div style={{ marginBottom: '15px', minWidth: '300px' }}>
                {quizCount ? (
                  <LoadingButton
                    fullWidth
                    variant="contained"
                    color="primary"
                    loading={submissionState.status === 'loading'}
                    onClick={onAttempt}
                  >
                    Start a Quiz
                  </LoadingButton>
                ) : (
                  <Tooltip title="Quiz Upcoming" placement="top">
                    <LoadingButton
                      fullWidth
                      variant="contained"
                      loading={submissionState.status === 'loading'}
                      style={{
                        color: TP_COLORS.black,
                        backgroundColor: TP_COLORS.tutopiyaGreenMain.dark2,
                        opacity: 0.7,
                      }}
                    >
                      Start a Quiz
                    </LoadingButton>
                  </Tooltip>
                )}
              </div>
            )}
            <div style={{ marginBottom: '15px', minWidth: '300px' }}>
              <LoadingButton
                fullWidth
                variant="contained"
                color="secondary"
                loading={quizState.status === 'loading'}
                onClick={onViewHistory}
                disabled={isDiagnostic && paperData.options.length === 0}
              >
                View History
              </LoadingButton>
            </div>
          </Stack>
        </>
      )}
    </div>
  );
};

export default ViewSubmissionsBySubTopic;
